import produce from 'immer';
import { handleActions } from 'redux-actions';
import { omit, reverse } from 'lodash';
import { CONFIRM_TAG_LIST_TYPE, PRODUCT_TAG_GROUP_KIND_LIST } from '../../../constants/service/app/tagConstants';
import actions from './actions';
import {
  PRODUCT_DETAIL_MODEL,
  PRODUCT_LIST_SEARCH_DATA_MODEL,
  PRODUCT_LIST_FILTER_OPTION_MODEL,
  PRODUCT_LIST_SORT_OPTION_MODEL,
  PRODUCT_STOCK_AND_OPTION_MODEL,
  PRODUCT_VERSION_MODEL,
  PRODUCT_LIST_SEARCH_OPTION_MODEL,
  PRODUCT_BULK_EDIT_DATA_MODEL,
  PRODUCT_OPTION_ITEM_MODEL,
} from './models';
import { LIST_PAGINATION_MODEL, LIST_SUMMARY_MODEL } from '../app/models';
import {
  FULFILLMENT_PRODUCT_MANAGEMENT_TYPE_LIST,
  PRODUCT_MANAGEMENT_TYPE,
  PRODUCT_PAY_METHOD_VALUE_TYPE,
} from '../../../constants/service/app/productConstants';
import { PAY_METHOD_TYPE } from '../../../constants/service/app/orderConstants';
import { addAllItemToSelectList, updateStoreWithPath } from '../../../lib/common';
import { setChoiceOption, setProductVersionInfo } from '../../../lib/utils';

/**
 *  INITIAL STATE
 */
const initialState = {
  /**
   *  목록 페이지 url
   */
  listPageUrl: null,

  /**
   *  상품 리스트
   */
  productList: {
    /**
     *  리스트
     */
    list: [],
    /**
     *  태그 그룹 리스트
     */
    tagGroupList: [],
    /**
     * 태그 리스트
     */
    tagList: [],
    /**
     *  태그 검색 리스트
     */
    tagSearchList: [],
    /**
     *  상품 1차 카테고리 리스트
     */
    mainProductCategorySelectList: [],
    /**
     *  상품 2차 카테고리 리스트
     */
    subProductCategorySelectList: {
      [null]: [],
    },
    /**
     *  검색 박스
     */
    searchData: PRODUCT_LIST_SEARCH_DATA_MODEL,
    /**
     *  검색 옵션
     */
    searchOption: PRODUCT_LIST_SEARCH_OPTION_MODEL,
    /**
     *  필터 옵션
     */
    filterOption: PRODUCT_LIST_FILTER_OPTION_MODEL,
    /**
     *  정렬 옵션 리스트
     */
    sortOption: PRODUCT_LIST_SORT_OPTION_MODEL,
    /**
     *  리스트 pagination
     */
    pagination: LIST_PAGINATION_MODEL,
    /**
     *  집계 데이터
     */
    summary: LIST_SUMMARY_MODEL,
  },

  /**
   *  상품 카테고리 SELECT LIST
   *  상품 리스트 검색 박스에서 사용
   */
  categorySelectList: [],

  /**
   *  상품 1차 카테고리 SELECT LIST
   */
  mainProductCategorySelectList: [],

  /**
   *  상품 2차 카테고리 SELECT LIST
   */
  subProductCategorySelectList: {
    null: [],
  },

  /**
   *  상품 결제 타입에 해당되는 결제수단 정보
   */
  productPayMethodTypeInfo: {
    1: {
      list: [PAY_METHOD_TYPE.CARD, PAY_METHOD_TYPE.TRANS, PAY_METHOD_TYPE.VBANK],
      card: PRODUCT_PAY_METHOD_VALUE_TYPE.ALL,
    },
  },

  /**
   *  상품 태그 그룹별 태그 SELECT LIST
   */
  productTagGroupList: {},

  /**
   *  판매가가 2,000원 이상만 올릴 수 있는 카테고리 id 리스트
   */
  minimumPrice2000WonCategoryIdList: [],

  /**
   *  상점 리스트
   */
  storeSelectList: [],

  /**
   *  상품 상세
   */
  productDetail: PRODUCT_DETAIL_MODEL,

  /**
   *  상품 버전
   */
  productVersion: PRODUCT_VERSION_MODEL,

  /**
   *  상품 통게
   */
  productStatistics: {
    /**
     *  인기점수
     */
    popularScore: [],
  },

  /**
   *  상품 거절 모달
   */
  rejectRegistrationProduct: {
    isOpen: false,
    data: {},
  },

  /**
   *  상품 판매 금지 모달
   */
  rejectBanProductModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  거절 사유 리스트 모달
   */
  rejectReasonListModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  상품 삭제 모달
   */
  deleteProductModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  엑셀 상품 할인 일괄 변경 모달
   */
  excelBulkEditProductDiscount: {
    isOpen: false,
    data: {},
  },

  /**
   *  엑셀 상품 할인 일괄 변경 성공 모달
   */
  excelBulkEditProductDiscountSuccessModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  엑셀 상품 할인 일괄 변경 실패 모달
   */
  excelBulkEditProductDiscountFailModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  상품 일괄 편집 모달
   */
  excelBulkEditProduct: PRODUCT_BULK_EDIT_DATA_MODEL,

  /**
   *  상품 속성 거절 모달
   */
  rejectRegistrationProductPropertyModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  2차 카테고리 보기 모달
   */
  productSubCategoryListViewModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  상품 옵션 순서 변경하는 모달
   */
  editProductOptionOrderModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  상품 결제 타입 편집 검증 모달
   */
  productPayMethodTypeEditConfirmModal: {
    isOpen: false,
    data: {},
  },
};

/**
 *  REDUCER
 */
export default handleActions(
  {
    /**
     *  [상품] 상품 데이터 변경하기
     */
    [actions.SET_DATA]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath(action.payload.path, action.payload.value, draft);
      });
    },

    /**
     *  [상품 리스트] 상품 리스트 받아오기
     */
    [actions.GET_PRODUCT_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  상품 전체 리스트
         */
        draft.productList = {
          ...draft.productList,
          ...action.payload.productList,
          mainProductCategorySelectList:
            action.payload.productList.mainProductCategorySelectList || state.productList.mainProductCategorySelectList,
          subProductCategorySelectList:
            action.payload.productList.subProductCategorySelectList || state.productList.subProductCategorySelectList,
        };
      });
    },

    /**
     *  [상품 버전] 상품 버전 정보 요청하기
     */
    [actions.GET_PRODUCT_VERSION_INFO_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  상품 버전 데이터 초기화
         */
        draft.productVersion = { ...initialState.productVersion };

        /**
         *  현재 버전 정보
         */
        draft.productVersion.currentVersionInfo = setProductVersionInfo(action.payload.currentVersionInfo);

        /**
         *  상품 버전 리스트
         */
        draft.productVersion.productVersionList = action.payload.productVersionList.map((item, index) => ({
          id: item.id,
          name: setProductVersionInfo(item, index + 1),
        }));

        /**
         *  상품 버전 리스트를 내림차순으로 정렬
         */
        draft.productVersion.productVersionList = reverse(draft.productVersion.productVersionList);

        /**
         *  상풍 버전 리스트에서 첫 번째 버전을 기본으로 보여줍니다.
         */
        draft.productVersion.targetVersionDetailId = draft.productVersion.productVersionList.length
          ? draft.productVersion.productVersionList[0].id
          : null;

        /**
         *  상품 1차 카테고리 리스트
         */
        draft.mainProductCategorySelectList = addAllItemToSelectList(
          action.payload.mainProductCategorySelectList,
          '카테고리 설정',
        );

        /**
         *  상품 2차 카테고리 리스트
         */
        for (const parentId in action.payload.subProductCategorySelectList) {
          action.payload.subProductCategorySelectList[parentId] = action.payload.subProductCategorySelectList[parentId]
            .length
            ? action.payload.subProductCategorySelectList[parentId]
            : addAllItemToSelectList(action.payload.subProductCategorySelectList[parentId]);
        }
        draft.subProductCategorySelectList = action.payload.subProductCategorySelectList;

        /**
         *  상품 태그 그룹별 태그
         */
        draft.productTagGroupList = action.payload.productTagGroupData.selectTagList;

        const { brandFeeInfoList } = action.payload.currentVersionDetail.proStockAndOption;
        const isFulfillmentProduct = brandFeeInfoList.find((item) =>
          FULFILLMENT_PRODUCT_MANAGEMENT_TYPE_LIST.includes(item.productManagementType),
        );

        /**
         *  default 수수료 정보
         */
        const targetFeeInfo = brandFeeInfoList.find(
          (item) => PRODUCT_MANAGEMENT_TYPE.ENTER === item.productManagementType,
        );

        /**
         *  현재 버전
         */
        draft.productVersion.currentVersionDetail = {
          ...action.payload.currentVersionDetail,
          proPayMethod: {
            ...draft.productVersion.currentVersionDetail.proPayMethod,
            ...action.payload.currentVersionDetail.proPayMethod,
          },
          proStockAndOption: {
            ...draft.productVersion.currentVersionDetail.proStockAndOption,
            ...action.payload.currentVersionDetail.proStockAndOption,
            optionList: action.payload.currentVersionDetail.proStockAndOption.optionList.length
              ? action.payload.currentVersionDetail.proStockAndOption.optionList.map((item) => ({
                  ...PRODUCT_OPTION_ITEM_MODEL,
                  ...item,
                  productManagementTypeSelectList: isFulfillmentProduct
                    ? PRODUCT_OPTION_ITEM_MODEL.productManagementTypeSelectList.map((subItem) => ({
                        ...subItem,
                        disabled: false,
                      }))
                    : PRODUCT_OPTION_ITEM_MODEL.productManagementTypeSelectList,
                }))
              : [],
            feeInfo: {
              ...draft.productVersion.currentVersionDetail.proStockAndOption.feeInfo,
              ...targetFeeInfo,
              ...action.payload.currentVersionDetail.proStockAndOption.feeInfo,
              productManagementTypeSelectList: isFulfillmentProduct
                ? draft.productVersion.currentVersionDetail.proStockAndOption.feeInfo.productManagementTypeSelectList.map(
                    (subItem) => ({
                      ...subItem,
                      disabled: false,
                    }),
                  )
                : draft.productVersion.currentVersionDetail.proStockAndOption.feeInfo.productManagementTypeSelectList,
            },
          },
        };

        /**
         *  상품 태그 분류에 따라서
         *  태그 검수 리스트 타입 설정
         */
        const tagGroupTagList = PRODUCT_TAG_GROUP_KIND_LIST.reduce((accumulator, tagGroupType) => {
          if (!draft.productVersion.currentVersionDetail.proInfo[tagGroupType]) {
            return accumulator;
          }

          accumulator[tagGroupType] = {
            ...draft.productVersion.currentVersionDetail.proInfo[tagGroupType],
            [CONFIRM_TAG_LIST_TYPE.ACTIVE]: draft.productVersion.currentVersionDetail.proInfo[tagGroupType][
              CONFIRM_TAG_LIST_TYPE.ACTIVE
            ].map((item) => ({
              id: item.id,
              name: item.name,
              groupId: item.groupId,
              groupName: item.groupName,
            })),
            confirmTagButtonType: CONFIRM_TAG_LIST_TYPE.ACTIVE,
          };
          return accumulator;
        }, {});

        draft.productVersion.currentVersionDetail.proInfo = {
          ...draft.productVersion.currentVersionDetail.proInfo,
          ...tagGroupTagList,
        };

        /**
         *  현재 버전에 옵션 리스트가 있다면 choiceOption 에 값 설정해주기
         */
        if (action.payload.currentVersionDetail.proStockAndOption.isOption) {
          draft.productVersion.currentVersionDetail.proStockAndOption.choiceOption = setChoiceOption(
            action.payload.currentVersionDetail.proStockAndOption,
          );

          /**
           *  옵션 리스트에서 id 값 빼주기
           */
          draft.productVersion.currentVersionDetail.proStockAndOption.optionList =
            draft.productVersion.currentVersionDetail.proStockAndOption.optionList.map((item) =>
              omit(item, ['id', 'productOptionKeyId']),
            );
        } else {
          draft.productVersion.currentVersionDetail.proStockAndOption.choiceOption =
            PRODUCT_STOCK_AND_OPTION_MODEL.choiceOption;
        }

        /**
         *  비교할 대상 버전
         */
        if (action.payload.targetVersionDetail) {
          draft.productVersion.targetVersionDetail = {
            ...action.payload.targetVersionDetail,
            proPayMethod: {
              ...draft.productVersion.targetVersionDetail.proPayMethod,
              ...action.payload.targetVersionDetail.proPayMethod,
            },
            proStockAndOption: {
              ...draft.productVersion.targetVersionDetail.proStockAndOption,
              ...action.payload.targetVersionDetail.proStockAndOption,
              optionList: action.payload.targetVersionDetail.proStockAndOption.optionList.length
                ? action.payload.targetVersionDetail.proStockAndOption.optionList.map((item) => ({
                    ...PRODUCT_OPTION_ITEM_MODEL,
                    ...item,
                    productManagementTypeSelectList: isFulfillmentProduct
                      ? PRODUCT_OPTION_ITEM_MODEL.productManagementTypeSelectList.map((subItem) => ({
                          ...subItem,
                          disabled: false,
                        }))
                      : PRODUCT_OPTION_ITEM_MODEL.productManagementTypeSelectList,
                  }))
                : [],
              feeInfo: {
                ...draft.productVersion.targetVersionDetail.proStockAndOption.feeInfo,
                ...targetFeeInfo,
                ...action.payload.targetVersionDetail.proStockAndOption.feeInfo,
                productManagementTypeSelectList: isFulfillmentProduct
                  ? draft.productVersion.targetVersionDetail.proStockAndOption.feeInfo.productManagementTypeSelectList.map(
                      (subItem) => ({
                        ...subItem,
                        disabled: false,
                      }),
                    )
                  : draft.productVersion.targetVersionDetail.proStockAndOption.feeInfo.productManagementTypeSelectList,
              },
            },
          };

          /**
           *  상품 태그 분류에 따라서
           *  태그 검수 리스트 타입 설정
           */
          const tagGroupTagList = PRODUCT_TAG_GROUP_KIND_LIST.reduce((accumulator, tagGroupType) => {
            if (!draft.productVersion.targetVersionDetail.proInfo[tagGroupType]) {
              return accumulator;
            }

            accumulator[tagGroupType] = {
              ...draft.productVersion.targetVersionDetail.proInfo[tagGroupType],
              confirmTagButtonType: CONFIRM_TAG_LIST_TYPE.ACTIVE,
            };
            return accumulator;
          }, {});

          draft.productVersion.targetVersionDetail.proInfo = {
            ...draft.productVersion.targetVersionDetail.proInfo,
            ...tagGroupTagList,
          };

          /**
           *  비교할 대상 버전에 옵션 리스트가 있다면 choiceOption 에 값 설정해주기
           */
          if (action.payload.targetVersionDetail.proStockAndOption.isOption) {
            draft.productVersion.targetVersionDetail.proStockAndOption.choiceOption = setChoiceOption(
              action.payload.targetVersionDetail.proStockAndOption,
            );

            /**
             *  옵션 리스트에서 id 값 빼주기
             */
            draft.productVersion.targetVersionDetail.proStockAndOption.optionList =
              draft.productVersion.targetVersionDetail.proStockAndOption.optionList.map((item) =>
                omit(item, ['id', 'productOptionKeyId']),
              );
          } else {
            draft.productVersion.targetVersionDetail.proStockAndOption.choiceOption =
              PRODUCT_STOCK_AND_OPTION_MODEL.choiceOption;
          }

          /**
           *  비교할 대상 버전의 거절 사유
           */
          if (action.payload.rejectReason && action.payload.rejectReason.reason)
            draft.productVersion.rejectReason = action.payload.rejectReason;
        } else draft.productVersion.targetVersionDetail = { ...initialState.productVersion.targetVersionDetail };
      });
    },

    /**
     *  [상품 버전] 상품 버전 상세 요청하기
     */
    [actions.GET_PRODUCT_VERSION_DETAIL_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        /**
         *  비교할 대상 버전
         */
        if (action.payload.targetVersionDetail) {
          const { brandFeeInfoList } = action.payload.targetVersionDetail.proStockAndOption;
          const isFulfillmentProduct = brandFeeInfoList.find((item) =>
            FULFILLMENT_PRODUCT_MANAGEMENT_TYPE_LIST.includes(item.productManagementType),
          );

          /**
           *  default 수수료 정보
           */
          const targetFeeInfo = brandFeeInfoList.find(
            (item) => PRODUCT_MANAGEMENT_TYPE.ENTER === item.productManagementType,
          );

          draft.productVersion.targetVersionDetail = {
            ...initialState.productVersion.targetVersionDetail,
            ...action.payload.targetVersionDetail,
            proPayMethod: {
              ...draft.productVersion.targetVersionDetail.proPayMethod,
              ...action.payload.targetVersionDetail.proPayMethod,
            },
            proStockAndOption: {
              ...initialState.productVersion.targetVersionDetail.proStockAndOption,
              ...action.payload.targetVersionDetail.proStockAndOption,
              optionList: action.payload.targetVersionDetail.proStockAndOption.optionList.length
                ? action.payload.targetVersionDetail.proStockAndOption.optionList.map((item) => ({
                    ...PRODUCT_OPTION_ITEM_MODEL,
                    ...item,
                    productManagementTypeSelectList: isFulfillmentProduct
                      ? PRODUCT_OPTION_ITEM_MODEL.productManagementTypeSelectList.map((subItem) => ({
                          ...subItem,
                          disabled: false,
                        }))
                      : PRODUCT_OPTION_ITEM_MODEL.productManagementTypeSelectList,
                  }))
                : [],
              feeInfo: {
                ...initialState.productVersion.targetVersionDetail.proStockAndOption.feeInfo,
                ...targetFeeInfo,
                ...action.payload.targetVersionDetail.proStockAndOption.feeInfo,
                productManagementTypeSelectList: isFulfillmentProduct
                  ? initialState.productVersion.targetVersionDetail.proStockAndOption.feeInfo.productManagementTypeSelectList.map(
                      (subItem) => ({
                        ...subItem,
                        disabled: false,
                      }),
                    )
                  : initialState.productVersion.targetVersionDetail.proStockAndOption.feeInfo
                      .productManagementTypeSelectList,
              },
            },
          };
        } else {
          draft.productVersion.targetVersionDetail = { ...initialState.productVersion.targetVersionDetail };
        }

        /**
         *  상품 태그 분류에 따라서
         *  태그 검수 리스트 타입 설정
         */
        const tagGroupTagList = PRODUCT_TAG_GROUP_KIND_LIST.reduce((accumulator, tagGroupType) => {
          if (!draft.productVersion.targetVersionDetail.proInfo[tagGroupType]) {
            return accumulator;
          }

          accumulator[tagGroupType] = {
            ...draft.productVersion.targetVersionDetail.proInfo[tagGroupType],
            confirmTagButtonType: CONFIRM_TAG_LIST_TYPE.ACTIVE,
          };
          return accumulator;
        }, {});

        draft.productVersion.targetVersionDetail.proInfo = {
          ...draft.productVersion.targetVersionDetail.proInfo,
          ...tagGroupTagList,
        };

        /**
         *  비교할 대상 버전에 옵션 리스트가 있다면 choiceOption 에 값 설정해주기
         */
        if (action.payload.targetVersionDetail.proStockAndOption.isOption) {
          draft.productVersion.targetVersionDetail.proStockAndOption.choiceOption = setChoiceOption(
            action.payload.targetVersionDetail.proStockAndOption,
          );

          /**
           *  옵션 리스트에서 id 값 빼주기
           */
          draft.productVersion.targetVersionDetail.proStockAndOption.optionList =
            draft.productVersion.targetVersionDetail.proStockAndOption.optionList.map((item) =>
              omit(item, ['id', 'productOptionKeyId']),
            );
        } else {
          draft.productVersion.targetVersionDetail.proStockAndOption.choiceOption =
            PRODUCT_STOCK_AND_OPTION_MODEL.choiceOption;
        }

        /**
         *  비교할 대상 버전의 id
         */
        draft.productVersion.targetVersionDetailId = action.payload.targetVersionDetailId;

        /**
         *  비교할 대상 버전의 거절 사유
         */
        if (action.payload.rejectReason && action.payload.rejectReason.reason)
          draft.productVersion.rejectReason = action.payload.rejectReason;
      });
    },

    /**
     *  엑셀 상품 일괄 편집 유효성 결과 가져오기
     */
    [actions.GET_BULK_EDIT_PRODUCT_VALIDATE_RESULT_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.excelBulkEditProduct = {
          ...draft.excelBulkEditProduct,
          bulkEditProductValidResultList: action.payload.bulkEditProductValidResultList,
          bulkEditProductValidResultCount: action.payload.bulkEditProductValidResultCount,
        };
      });
    },

    /**
     *  모달 토글
     */
    [actions.TOGGLE_MODAL]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath([action.payload.name, 'data'], action.payload.data, draft);
        updateStoreWithPath([action.payload.name, 'isOpen'], action.payload.value, draft);
      });
    },

    /**
     * 상품리스트 초기값으로 리셋
     */
    [actions.RESET_PRODUCT_LIST]: (state) => {
      return produce(state, (draft) => {
        draft.productList = { ...initialState.productList };
      });
    },
  },
  initialState,
);
